import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabaseClient'
import EditableCell from './EditableCell'
import ExportModal from './ExportModal'

const statusOptions = ['Invoiced', 'Paid', 'Overdue', 'Cancelled']
const typeOptions = ['Article', 'Blog Post', 'Case Study', 'Copywriting', 'Email', 'White Paper', 'Social Media', 'Other']

const presetRanges = [
  { label: 'All Time',      value: 'all' },
  { label: 'This Year',     value: 'this_year' },
  { label: 'Last 6 Months', value: 'last_6_months' },
  { label: 'Last 3 Months', value: 'last_3_months' },
  { label: 'Last Month',    value: 'last_month' },
  { label: 'Last Week',     value: 'last_week' },
  { label: 'Custom Range',  value: 'custom' },
]

function getDateRange(preset) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  switch (preset) {
    case 'this_year':
      return { start: new Date(now.getFullYear(), 0, 1), end: null }
    case 'last_6_months': {
      const d = new Date(today); d.setMonth(d.getMonth() - 6)
      return { start: d, end: null }
    }
    case 'last_3_months': {
      const d = new Date(today); d.setMonth(d.getMonth() - 3)
      return { start: d, end: null }
    }
    case 'last_month': {
      const d = new Date(today); d.setMonth(d.getMonth() - 1)
      return { start: d, end: null }
    }
    case 'last_week': {
      const d = new Date(today); d.setDate(d.getDate() - 7)
      return { start: d, end: null }
    }
    default:
      return { start: null, end: null }
  }
}

function ClientInput({ value, onChange, theme, clients, inputStyle }) {
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleChange = (e) => {
    const v = e.target.value
    onChange(v)
    if (v.length > 0) {
      const matches = clients.filter(c => c.toLowerCase().includes(v.toLowerCase()))
      setSuggestions(matches)
      setShowSuggestions(matches.length > 0)
    } else {
      setSuggestions(clients)
      setShowSuggestions(clients.length > 0)
    }
  }

  const handleFocus = () => {
    const matches = value.length > 0
      ? clients.filter(c => c.toLowerCase().includes(value.toLowerCase()))
      : clients
    setSuggestions(matches)
    setShowSuggestions(matches.length > 0)
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        placeholder="e.g. HubSpot"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        autoComplete="off"
      />
      {showSuggestions && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.surface, border: `1px solid ${theme.border}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 200,
          maxHeight: 180, overflowY: 'auto',
        }}>
          {suggestions.map(name => (
            <div key={name} onMouseDown={() => { onChange(name); setShowSuggestions(false) }}
              style={{
                padding: '9px 12px', fontSize: 13,
                fontFamily: "'Nunito Sans', sans-serif",
                color: theme.text, cursor: 'pointer',
                borderBottom: `1px solid ${theme.border}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{name}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Income({ session, theme, canEdit = true }) {
  const [income, setIncome] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [taxRate, setTaxRate] = useState(20)
  const [editingTax, setEditingTax] = useState(false)
  const [taxInput, setTaxInput] = useState('20')
  const [preset, setPreset] = useState('all')
  const [showExport, setShowExport] = useState(false)
  const [sortField, setSortField] = useState('invoice_date')
  const [sortDir, setSortDir] = useState('desc')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [form, setForm] = useState({
    client: '', project: '', amount: '', type: 'Article',
    invoice_date: '', date_paid: '', status: 'Invoiced', notes: ''
  })

  const statusColors = {
    'Invoiced':  { bg: theme.statusBlueBg,   text: theme.statusBlueText   },
    'Paid':      { bg: theme.statusGreenBg,  text: theme.statusGreenText  },
    'Overdue':   { bg: theme.statusYellowBg, text: theme.statusYellowText },
    'Cancelled': { bg: theme.statusGrayBg,   text: theme.statusGrayText   },
  }

  useEffect(() => {
    if (session?.user?.id) {
      fetchIncome()
      fetchSettings()
      fetchClients()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchIncome = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('income').select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (!error) setIncome(data)
    setLoading(false)
  }

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('settings').select('tax_rate')
      .eq('user_id', session.user.id).single()
    if (data) { setTaxRate(data.tax_rate); setTaxInput(String(data.tax_rate)) }
  }

  const fetchClients = async () => {
    const { data } = await supabase
      .from('clients').select('name')
      .eq('user_id', session.user.id)
      .order('name', { ascending: true })
    if (data) setClients(data.map(c => c.name))
  }

  const saveClient = async (name) => {
    if (!name?.trim()) return
    await supabase.from('clients')
      .upsert({ user_id: session.user.id, name: name.trim() }, { onConflict: 'user_id,name' })
    fetchClients()
  }

  const saveTaxRate = async (rate) => {
    const parsed = parseFloat(rate)
    if (isNaN(parsed) || parsed < 0 || parsed > 100) return
    setTaxRate(parsed)
    setEditingTax(false)
    await supabase.from('settings')
      .upsert({ user_id: session.user.id, tax_rate: parsed }, { onConflict: 'user_id' })
  }

  const handleSave = async () => {
    if (!form.client) return
    setSaving(true)
    const { error } = await supabase.from('income').insert([{
      client: form.client, project: form.project || null,
      amount: form.amount || 0, type: form.type,
      invoice_date: form.invoice_date || null, date_paid: form.date_paid || null,
      status: form.status, notes: form.notes || null, user_id: session.user.id,
    }])
    if (!error) {
      await saveClient(form.client)
      setForm({ client: '', project: '', amount: '', type: 'Article', invoice_date: '', date_paid: '', status: 'Invoiced', notes: '' })
      setShowForm(false)
      fetchIncome()
    }
    setSaving(false)
  }

  const handleUpdate = async (id, field, value) => {
    await supabase.from('income').update({ [field]: value || null }).eq('id', id)
    if (field === 'client') await saveClient(value)
    await fetchIncome()
  }

  const handleDelete = async (id) => {
    const { error } = await supabase.from('income').delete().eq('id', id).eq('user_id', session.user.id)
    if (!error) fetchIncome()
  }

  const getFilteredIncome = (data) => {
    if (preset === 'all') return data
    let start, end
    if (preset === 'custom') {
      start = customStart ? new Date(customStart) : null
      end = customEnd ? new Date(customEnd) : null
    } else {
      const range = getDateRange(preset)
      start = range.start; end = range.end
    }
    return data.filter(entry => {
      const dateToUse = entry.date_paid || entry.invoice_date
      if (!dateToUse) return false
      const d = new Date(dateToUse + 'T12:00:00')
      if (start && d < start) return false
      if (end && d > end) return false
      return true
    })
  }

  const filteredIncome = getFilteredIncome(income)

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const sortedIncome = [...filteredIncome].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    // Nulls always to the bottom
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return 1
    if (bVal == null) return -1
    // Numeric
    if (sortField === 'amount') {
      aVal = Number(aVal); bVal = Number(bVal)
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    }
    // Date fields
    if (sortField === 'invoice_date' || sortField === 'date_paid') {
      aVal = new Date(aVal); bVal = new Date(bVal)
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    }
    // String fields
    aVal = String(aVal).toLowerCase(); bVal = String(bVal).toLowerCase()
    if (aVal < bVal) return sortDir === 'desc' ? 1 : -1
    if (aVal > bVal) return sortDir === 'desc' ? -1 : 1
    return 0
  })
  const currentYear = new Date().getFullYear()
  const earnedThisYear = income
    .filter(i => i.status === 'Paid' && i.date_paid && new Date(i.date_paid).getFullYear() === currentYear)
    .reduce((sum, i) => sum + (Number(i.amount) || 0), 0)
  const totalEarned    = filteredIncome.filter(i => i.status === 'Paid').reduce((sum, i) => sum + (Number(i.amount) || 0), 0)
  const totalInvoiced  = filteredIncome.filter(i => i.status === 'Invoiced').reduce((sum, i) => sum + (Number(i.amount) || 0), 0)
  const totalOverdue   = filteredIncome.filter(i => i.status === 'Overdue').reduce((sum, i) => sum + (Number(i.amount) || 0), 0)
  const postTax = totalEarned * (1 - taxRate / 100)
  const selectedPresetLabel = presetRanges.find(r => r.value === preset)?.label || 'All Time'

  const inputStyle = {
    width: '100%', background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
    color: theme.text, padding: '9px 12px', fontSize: 13,
    fontFamily: "'Nunito Sans', sans-serif", boxSizing: 'border-box', outline: 'none',
  }

  const labelStyle = {
    fontSize: 10, color: theme.textSecondary, marginBottom: 5,
    letterSpacing: '0.1em', fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
  }

  return (
    <div>
      {/* Date filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', marginRight: 4 }}>Period:</div>
        {presetRanges.map(r => (
          <button key={r.value} onClick={() => setPreset(r.value)} style={{
            background: preset === r.value ? theme.accent : theme.surface,
            color: preset === r.value ? '#0a2342' : theme.textSecondary,
            border: `1px solid ${preset === r.value ? theme.accent : theme.border}`,
            padding: '5px 12px', fontSize: 11, fontFamily: "'Open Sans', sans-serif",
            letterSpacing: '0.08em', cursor: 'pointer', fontWeight: preset === r.value ? 700 : 400,
          }}>{r.label}</button>
        ))}
        {preset === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, color: theme.text, padding: '5px 8px', fontSize: 12, fontFamily: "'Nunito Sans', sans-serif", outline: 'none' }} />
            <span style={{ color: theme.textMuted, fontSize: 12 }}>to</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, color: theme.text, padding: '5px 8px', fontSize: 12, fontFamily: "'Nunito Sans', sans-serif", outline: 'none' }} />
          </div>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
        <div style={{ background: theme.surface, border: `2px solid ${theme.accent}55`, padding: '18px 20px' }}>
          <div style={{ fontSize: 10, color: theme.accent, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 4, fontWeight: 700 }}>Est. Take-Home</div>
          <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Open Sans', sans-serif", marginBottom: 4, letterSpacing: '0.05em' }}>{selectedPresetLabel}</div>
          <div style={{ fontSize: 26, color: theme.accent, fontFamily: "'Cardo', serif", fontWeight: 700, marginBottom: 8 }}>${Math.round(postTax).toLocaleString()}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {editingTax ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" value={taxInput} onChange={e => setTaxInput(e.target.value)}
                  onBlur={() => saveTaxRate(taxInput)} onKeyDown={e => e.key === 'Enter' && saveTaxRate(taxInput)} autoFocus
                  style={{ width: 52, background: theme.surfaceAlt, border: `1px solid ${theme.accent}`, color: theme.text, padding: '2px 6px', fontSize: 12, fontFamily: "'Nunito Sans', sans-serif", outline: 'none' }} />
                <span style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>% tax</span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span onClick={() => setEditingTax(true)} style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", cursor: 'pointer', borderBottom: `1px dashed ${theme.textMuted}` }} title="Click to update your tax rate">{taxRate}% tax rate</span>
                <a href="https://www.irs.gov/businesses/small-businesses-self-employed/self-employment-tax-social-security-and-medicare-taxes" target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: theme.accent, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.05em', textDecoration: 'none', opacity: 0.7 }}>IRS guide ↗</a>
              </div>
            )}
          </div>
        </div>

        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '18px 20px' }}>
          <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8 }}>Earned {currentYear}</div>
          <div style={{ fontSize: 26, color: theme.text, fontFamily: "'Cardo', serif" }}>${earnedThisYear.toLocaleString()}</div>
        </div>

        {[
          { label: 'Total Earned',     value: `$${totalEarned.toLocaleString()}` },
          { label: 'Awaiting Payment', value: `$${totalInvoiced.toLocaleString()}` },
          { label: 'Overdue',          value: `$${totalOverdue.toLocaleString()}`, alert: totalOverdue > 0 },
        ].map((stat, i) => (
          <div key={i} style={{ background: theme.surface, border: `1px solid ${stat.alert ? '#ef444444' : theme.border}`, padding: '18px 20px' }}>
            <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 4 }}>{stat.label}</div>
            <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Open Sans', sans-serif", marginBottom: 4, letterSpacing: '0.05em' }}>{selectedPresetLabel}</div>
            <div style={{ fontSize: 26, color: stat.alert ? '#ef4444' : theme.text, fontFamily: "'Cardo', serif" }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif" }}>
          {filteredIncome.length} {filteredIncome.length === 1 ? 'entry' : 'entries'}
          {preset !== 'all' && <span style={{ color: theme.accent, marginLeft: 6 }}>· {selectedPresetLabel}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setShowExport(true)}
            style={{
              background: 'none',
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              color: theme.text,
              cursor: 'pointer',
              opacity: filteredIncome.length === 0 ? 0.4 : 1,
              pointerEvents: filteredIncome.length === 0 ? 'none' : 'auto',
            }}
          >
            Export CSV
          </button>
          {canEdit && (
            <button onClick={() => setShowForm(!showForm)} style={{
              background: theme.accent, color: '#0a2342', border: 'none',
              padding: '8px 18px', fontSize: 11, letterSpacing: '0.1em',
              textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: 'pointer',
            }}>{showForm ? 'Cancel' : '+ Add Income'}</button>
          )}
        </div>
      </div>

      {/* New income form */}
      {showForm && (
        <div style={{ background: theme.surface, border: `1px solid ${theme.accent}44`, padding: '24px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 20 }}>New Income Entry</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><div style={labelStyle}>Client *</div>
              <ClientInput value={form.client} onChange={v => setForm({ ...form, client: v })} theme={theme} clients={clients} inputStyle={inputStyle} /></div>
            <div><div style={labelStyle}>Project</div>
              <input style={inputStyle} placeholder="e.g. Q1 Blog Posts" value={form.project} onChange={e => setForm({ ...form, project: e.target.value })} /></div>
            <div><div style={labelStyle}>Amount ($)</div>
              <input type="number" style={inputStyle} placeholder="e.g. 500" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
            <div><div style={labelStyle}>Type</div>
              <select style={inputStyle} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                {typeOptions.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><div style={labelStyle}>Invoice Date</div>
              <input type="date" style={inputStyle} value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></div>
            <div><div style={labelStyle}>Date Paid</div>
              <input type="date" style={inputStyle} value={form.date_paid} onChange={e => setForm({ ...form, date_paid: e.target.value })} /></div>
            <div><div style={labelStyle}>Status</div>
              <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {statusOptions.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><div style={labelStyle}>Notes</div>
              <input style={inputStyle} placeholder="e.g. Net 30" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <button onClick={handleSave} disabled={saving || !form.client} style={{
            background: form.client ? theme.accent : theme.border, color: '#0a2342', border: 'none',
            padding: '9px 22px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: form.client ? 'pointer' : 'not-allowed',
          }}>{saving ? 'Saving...' : 'Save Entry'}</button>
        </div>
      )}

      {filteredIncome.length > 0 && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.05em', fontStyle: 'italic' }}>
          Click any field to edit it inline
        </div>
      )}

      {/* Income table */}
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr 1fr 1fr 120px 40px',
          padding: '11px 20px', borderBottom: `1px solid ${theme.border}`,
          fontSize: 12, color: theme.textMuted, letterSpacing: '0.12em',
          textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif",
        }}>
          {[
            { label: 'Client',       field: 'client'       },
            { label: 'Project',      field: 'project'      },
            { label: 'Type',         field: 'type'         },
            { label: 'Amount',       field: 'amount'       },
            { label: 'Invoice Date', field: 'invoice_date' },
            { label: 'Date Paid',    field: 'date_paid'    },
            { label: 'Status',       field: 'status'       },
          ].map(col => (
            <span
              key={col.field}
              onClick={() => handleSort(col.field)}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {col.label}
              {sortField === col.field
                ? <span style={{ opacity: 0.8 }}>{sortDir === 'desc' ? ' ↓' : ' ↑'}</span>
                : <span style={{ opacity: 0.25 }}> ↕</span>}
            </span>
          ))}
          <span></span>
        </div>

        {loading && <div style={{ padding: '32px', textAlign: 'center', color: theme.textMuted, fontSize: 13, fontFamily: "'Nunito Sans', sans-serif" }}>Loading income...</div>}

        {!loading && filteredIncome.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginBottom: 8 }}>
              {income.length === 0 ? 'No income entries yet' : 'No entries for this period'}
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", opacity: 0.6 }}>
              {income.length === 0 ? 'Click "+ Add Income" to start tracking your earnings' : 'Try selecting a different date range'}
            </div>
          </div>
        )}

        {!loading && sortedIncome.map((entry, i) => (
          <div key={entry.id} style={{
            display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr 1fr 1fr 120px 40px',
            padding: theme.rowPadding, borderBottom: i < sortedIncome.length - 1 ? `1px solid ${theme.border}` : 'none',
            alignItems: 'center',
          }}>
            <EditableCell value={entry.client} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'client', v)} />
            <EditableCell value={entry.project} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'project', v)} />
            <EditableCell value={entry.type} theme={theme} options={typeOptions} canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'type', v)} />
            <EditableCell value={entry.amount} theme={theme} type="number" isAmount canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'amount', v)} />
            <EditableCell value={entry.invoice_date} theme={theme} type="date" canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'invoice_date', v)} />
            <EditableCell value={entry.date_paid} theme={theme} type="date" canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'date_paid', v)} />
            <EditableCell value={entry.status} theme={theme} isStatus statusOptions={statusOptions} statusColors={statusColors} canEdit={canEdit} onSave={v => handleUpdate(entry.id, 'status', v)} />
            {canEdit && <button onClick={() => handleDelete(entry.id)} style={{ background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 16, padding: '0 4px', opacity: 0.5 }} title="Delete entry">×</button>}
          </div>
        ))}
      </div>
      {showExport && (
        <ExportModal
          entries={sortedIncome}
          filterLabel={preset === 'all' ? null : preset}
          theme={theme}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  )
}